import { useMemo, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import { MessageSquare } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import ImageLightbox from '@/components/ImageLightbox'
import VideoLightbox from '@/components/VideoLightbox'
import FilterSidebar from '@/components/inbox/FilterSidebar'
import ConversationList from '@/components/inbox/ConversationList'
import ChatPanel from '@/components/inbox/ChatPanel'
import UserDetailsPanel from '@/components/inbox/UserDetailsPanel'
import ComposePanel from '@/components/inbox/ComposePanel'
import { InboxProvider } from '@/components/inbox/InboxContext'
import { useInboxState } from '@/hooks/useInboxState'
import { useStableCallback } from '@/hooks/useStableCallback'
import type { InboxProps } from '@/components/inbox/types'

export default function Inbox(props: InboxProps) {
  const {
    currentAgent,
    agents,
    filter,
    statusFilter,
    teamFilter,
    channelFilter,
    selectedConversationId,
    macros,
    whatsappTemplates,
    whatsappConfigurations,
  } = props

  const state = useInboxState(props)

  // --- Stabilize callbacks so they don't invalidate the memoized context value ---
  const onSendMessage = useStableCallback(state.handleSendMessage)
  const onRetryMessage = useStableCallback(state.handleRetryMessage)
  const onCloseConversation = useStableCallback(state.handleCloseConversation)
  const onCloseWithRating = useStableCallback(state.handleCloseWithRating)
  const onReopenConversation = useStableCallback(state.handleReopenConversation)
  const onUnsnoozeConversation = useStableCallback(state.handleUnsnoozeConversation)
  const onDeleteMessage = useStableCallback(state.deleteMessage)
  const onSendNote = useStableCallback(state.handleSendNote)
  const onRetryNote = useStableCallback(state.handleRetryNote)
  const onVideoClick = useStableCallback(state.handleVideoClick)
  const saveDraft = useStableCallback(state.saveDraft)
  const stableNotifyTyping = useStableCallback(state.notifyTyping)
  // useState setters are stable, so no dependencies needed
  const onToggleDetailsPanel = useCallback(
    () => state.setIsDetailsPanelOpen(true),
    [],
  )

  // Only expose lightbox.open to context (already a stable useCallback)
  const stableLightbox = useMemo(
    () => ({ open: state.lightbox.open }),
    [state.lightbox.open],
  )

  // Memoize InboxProvider value so it only recomputes when actual data or
  // picker state changes, not on unrelated state (conversationPresence, stats).
  const inboxContextValue = useMemo(() => {
    if (!state.selectedConversation) return null
    return {
      conversation: state.selectedConversation,
      messages: state.messages,
      internalNotes: state.internalNotes,
      currentAgent,
      filter,
      statusFilter,
      macros,
      whatsappTemplates,
      typingUsers: state.typingUsers,
      notifyTyping: stableNotifyTyping,
      messagesEndRef: state.messagesEndRef,
      messagesContainerRef: state.messagesContainerRef,
      onCloseConversation,
      onCloseWithRating,
      onReopenConversation,
      onUnsnoozeConversation,
      snoozePicker: state.snoozePicker,
      snoozeButtonRef: state.snoozeButtonRef,
      isDetailsPanelOpen: state.isDetailsPanelOpen,
      onToggleDetailsPanel,
      onSendMessage,
      onRetryMessage,
      saveDraft,
      onDeleteMessage,
      bodyRef: state.bodyRef,
      draftPlainTextRef: state.draftPlainTextRef,
      pendingAttachments: state.pendingAttachments,
      setPendingAttachments: state.setPendingAttachments,
      chatEditorRef: state.chatEditorRef,
      replyingToMessage: state.replyingToMessage,
      setReplyingToMessage: state.setReplyingToMessage,
      lightbox: stableLightbox,
      onVideoClick,
      conversationSource: state.selectedConversation.source,
      onSendNote,
      onRetryNote,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.selectedConversation,
    state.messages,
    state.internalNotes,
    currentAgent,
    filter,
    statusFilter,
    macros,
    whatsappTemplates,
    state.typingUsers,
    state.isDetailsPanelOpen,
    state.pendingAttachments,
    state.replyingToMessage,
    // Snooze picker: track individual state values, not the object reference
    state.snoozePicker.isOpen,
    state.snoozePicker.mode,
    state.snoozePicker.isLoading,
    state.snoozePicker.selectedPresetIndex,
    state.snoozePicker.selectedDate,
    state.snoozePicker.selectedTimeIndex,
    state.snoozePicker.currentMonth,
  ])

  return (
    <>
      <Head title="Caixa de Entrada" />
      <div className="flex h-screen bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="inbox" />

        {/* Column 1: Filter Sidebar */}
        {state.isSidebarOpen && (
          <FilterSidebar
            filter={filter}
            statusFilter={statusFilter}
            teamFilter={teamFilter}
            channelFilter={channelFilter}
            stats={state.stats}
            teams={state.teams}
            selectedConversationId={selectedConversationId}
            buildFilterUrl={state.buildFilterUrl}
            onStartNewConversation={state.startNewConversation}
          />
        )}

        {/* Column 2: Conversation List */}
        <ConversationList
          conversations={state.filteredConversations}
          selectedConversationId={selectedConversationId}
          searchQuery={state.searchQuery}
          onSearchChange={state.setSearchQuery}
          onConversationClick={state.handleConversationClick}
          isSidebarOpen={state.isSidebarOpen}
          onToggleSidebar={() => state.setIsSidebarOpen(!state.isSidebarOpen)}
          statusFilter={statusFilter}
          stats={{ open: state.stats.open, closed: state.stats.closed, snoozed: state.stats.snoozed }}
          onStatusChange={state.handleStatusChange}
          selectedConversationIds={state.selectedConversationIds}
          onToggleSelection={state.toggleConversationSelection}
          onToggleSelectAll={state.toggleSelectAll}
          onDeselectAll={state.deselectAllConversations}
          agents={agents}
          currentAgentId={currentAgent.id}
          onBulkAssign={state.handleBulkAssign}
          onBulkUnassign={state.handleBulkUnassign}
          onBulkStatusChange={state.handleBulkStatusChange}
          loadMoreRef={state.loadMoreRef}
          isLoadingMore={state.isLoadingMore}
          hasMore={state.hasMore}
          totalCount={state.conversations.length}
          conversationPresence={state.conversationPresence}
        />

        {/* Column 3: Chat Area + User Panel */}
        {state.isComposingNew ? (
          <ComposePanel
            currentAgent={currentAgent}
            filter={filter}
            statusFilter={statusFilter}
            buildFilterUrl={state.buildFilterUrl}
            onCancel={state.cancelNewConversation}
            whatsappTemplates={whatsappTemplates}
            whatsappConfigurations={whatsappConfigurations}
            macros={macros}
          />
        ) : inboxContextValue ? (
          <InboxProvider value={inboxContextValue}>
            <ChatPanel />

            {/* User Details Panel */}
            {state.isDetailsPanelOpen && (
              <UserDetailsPanel
                conversation={state.selectedConversation!}
                agents={agents}
                currentAgentId={currentAgent.id}
                filter={filter}
                statusFilter={statusFilter}
                onAssignAgent={state.handleAssignAgent}
                onUnassignAgent={state.handleUnassignAgent}
                onClose={() => state.setIsDetailsPanelOpen(false)}
              />
            )}
          </InboxProvider>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white text-gray-500">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">Nenhuma conversa selecionada</p>
              <p className="text-sm">Selecione uma conversa da lista</p>
            </div>
          </div>
        )}
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={state.lightbox.isOpen}
        images={state.lightbox.images}
        currentIndex={state.lightbox.currentIndex}
        onClose={state.lightbox.close}
        onNext={state.lightbox.next}
        onPrev={state.lightbox.prev}
      />

      {/* Video Lightbox */}
      <VideoLightbox
        isOpen={state.videoLightbox.isOpen}
        videoUrl={state.videoLightbox.url}
        filename={state.videoLightbox.filename}
        onClose={state.closeVideoLightbox}
      />
    </>
  )
}
